import React, { useState } from 'react';
import './CommonStyles.css';
import './InvoiceSettings.css';
import { Edit3, Trash2, Info, ChevronDown, Download, Printer, FileText, FileSpreadsheet } from 'lucide-react';

const InvoiceSettings = () => {
    const [activeSubTab, setActiveSubTab] = useState('schemes');
    const [searchQuery, setSearchQuery] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAddLayoutModal, setShowAddLayoutModal] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState(null);
    const [expandedAccordion, setExpandedAccordion] = useState('general');
    const [selectedLayouts, setSelectedLayouts] = useState([]);

    // Action Dropdown state
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

    const toggleDropdown = (id, event) => {
        event.stopPropagation();
        if (openDropdownId === id) {
            setOpenDropdownId(null);
        } else {
            const rect = event.currentTarget.getBoundingClientRect();
            const gap = 8;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const menuMinWidth = 160;
            const edgePadding = 8;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            const openUpwards = spaceBelow < 150 && spaceAbove > spaceBelow;
            const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
            const maxHeight = Math.max(120, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

            const left = Math.min(
                Math.max(rect.left - 100, edgePadding),
                Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
            );

            setDropdownPosition({
                top: openUpwards ? null : (rect.bottom + gap),
                bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
                left,
                maxHeight
            });
            setOpenDropdownId(id);
        }
    };

    // Click outside handler
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openDropdownId) return;
            const clickedActionButton = event.target.closest('.action-btn-trigger');
            const clickedDropdownMenu = event.target.closest('.action-dropdown-menu');
            if (clickedActionButton || clickedDropdownMenu) return;
            setOpenDropdownId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdownId]);

    const schemes = [
        { id: 1, name: 'Default', prefix: '', type: 'Sequential', start: 1, count: 105, digits: 4, isDefault: false },
        { id: 2, name: 'PLB2025', prefix: '', type: 'Sequential', start: 0, count: 1, digits: 4, isDefault: true }
    ];

    const layouts = [
        { id: 1, name: "Lefty'S Liquor", isDefault: true },
        { id: 2, name: "Test123", isDefault: false },
        { id: 3, name: "Anupma Babaria", isDefault: false }
    ];

    const renderSchemesTable = () => (
        <div className="invoice-table-wrapper">
            <table className="invoice-table">
                <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        {['Name', 'Prefix', 'Numbering Type', 'Start from', 'Invoice Count', 'Number of digits', 'Action'].map((header, idx) => (
                            <th key={idx} style={{ padding: '1rem', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    {header}
                                    {header !== 'Action' && <Info size={14} style={{ color: '#06b6d4' }} />}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {schemes.map((scheme) => (
                        <tr key={scheme.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <strong>{scheme.name}</strong>
                                    {scheme.isDefault && (
                                        <span style={{ background: '#10b981', color: 'white', fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Default</span>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '1rem' }}>{scheme.prefix || '--'}</td>
                            <td style={{ padding: '1rem' }}>{scheme.type}</td>
                            <td style={{ padding: '1rem' }}>{scheme.start}</td>
                            <td style={{ padding: '1rem' }}>{scheme.count}</td>
                            <td style={{ padding: '1rem' }}>{scheme.digits}</td>
                            <td style={{ padding: '0.75rem 1rem' }} className="actions-cell">
                                <div className="action-dropdown-container">
                                    <button
                                        className="action-btn-trigger"
                                        onClick={(e) => toggleDropdown(`scheme-${scheme.id}`, e)}
                                    >
                                        Action <span className="arrow-down">▼</span>
                                    </button>
                                    {openDropdownId === `scheme-${scheme.id}` && (
                                        <div
                                            className="action-dropdown-menu"
                                            style={{
                                                position: 'fixed',
                                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                left: `${dropdownPosition.left}px`,
                                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                zIndex: 9999999
                                            }}
                                        >
                                            <button className="dropdown-item" onClick={() => setOpenDropdownId(null)}>
                                                <Edit3 size={14} style={{ marginRight: '8px' }} /> Edit
                                            </button>
                                            <button className="dropdown-item delete-item" onClick={() => setOpenDropdownId(null)}>
                                                <Trash2 size={14} style={{ marginRight: '8px' }} /> Delete
                                            </button>
                                            {!scheme.isDefault && (
                                                <button className="dropdown-item" onClick={() => setOpenDropdownId(null)}>
                                                    <Info size={14} style={{ marginRight: '8px', color: '#06b6d4' }} /> Set as default
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderAddModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '8px', width: '600px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b', fontWeight: '600' }}>Add new invoice scheme</h3>
                    <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Format Selection */}
                    <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', alignItems: 'flex-start' }}>
                        <div
                            onClick={() => setSelectedFormat('xxxx')}
                            style={{
                                width: '160px',
                                height: '100px',
                                background: '#e2e8f0',
                                borderRadius: '4px',
                                padding: '1rem',
                                cursor: 'pointer',
                                border: selectedFormat === 'xxxx' ? '2px solid #3b82f6' : '1px solid transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <div style={{ fontSize: '1.1rem', color: '#334155' }}>FORMAT:</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#334155' }}>XXXX</div>
                        </div>
                        <div
                            onClick={() => setSelectedFormat('year-xxxx')}
                            style={{
                                width: '160px',
                                height: '100px',
                                background: '#e2e8f0',
                                borderRadius: '4px',
                                padding: '1rem',
                                cursor: 'pointer',
                                border: selectedFormat === 'year-xxxx' ? '2px solid #3b82f6' : '1px solid transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <div style={{ fontSize: '1.1rem', color: '#334155' }}>FORMAT:</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#334155' }}>2026-XXXX</div>
                        </div>
                        <div style={{ marginLeft: '1rem' }}>
                            <div style={{ fontWeight: '700', color: '#101010', marginBottom: '0.25rem' }}>Preview:</div>
                            <div style={{ color: '#64748b' }}>{selectedFormat ? (selectedFormat === 'xxxx' ? '0001' : '2026-0001') : 'Not selected'}</div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>Name:*</label>
                        <input
                            type="text"
                            placeholder="Name"
                            style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>
                            Numbering Type:* <Info size={16} style={{ color: '#06b6d4' }} />
                        </label>
                        <div style={{ position: 'relative' }}>
                            <select style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none', background: '#fff', appearance: 'none' }}>
                                <option>Sequential</option>
                                <option>Aleatory/Random</option>
                            </select>
                            <ChevronDown size={16} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }} />
                        </div>
                    </div>
                </div>

                {/* Modal Footer - Buttons in Middle */}
                <div style={{ padding: '1rem 1.5rem', borderTop: 'none', display: 'flex', justifyContent: 'center', gap: '0.5rem', paddingBottom: '2rem' }}>
                    <button style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.5rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Save
                    </button>
                    <button onClick={() => setShowAddModal(false)} style={{ background: '#334155', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.5rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAddLayoutModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '8px', width: '900px', maxWidth: '95vw', height: '90vh', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b', fontWeight: '600' }}>Add new invoice layout</h3>
                    <button onClick={() => setShowAddLayoutModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                {/* Modal Body - Scrollable */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                    {/* Compact Accordion Section */}
                    {[
                        { id: 'general', title: 'General Settings', icon: '📝' },
                        { id: 'labels', title: 'Headings & Labels', icon: '🏷️' },
                        { id: 'address', title: 'Address & Communication', icon: '📍' },
                        { id: 'products', title: 'Product Details', icon: '📦' },
                        { id: 'totals', title: 'Totals, QR & Others', icon: '📊' }
                    ].map((section) => (
                        <div key={section.id} style={{ marginBottom: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                            <button
                                onClick={() => setExpandedAccordion(expandedAccordion === section.id ? null : section.id)}
                                style={{ width: '100%', padding: '1rem', background: '#f8fafc', border: 'none', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: '600', color: '#1e293b' }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <span style={{ fontSize: '1.2rem' }}>{section.icon}</span> {section.title}
                                </span>
                                <ChevronDown
                                    size={18}
                                    style={{ transform: expandedAccordion === section.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                />
                            </button>

                            {expandedAccordion === section.id && (
                                <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                                    {section.id === 'general' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>Layout name:*</label>
                                                <input type="text" placeholder="Layout name" style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>Design:*</label>
                                                <select style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                                                    <option>Classic (For normal printer)</option>
                                                </select>
                                                <small style={{ color: '#64748b' }}>Used for browser based printing</small>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                                <input type="checkbox" id="showLetterHead" />
                                                <label htmlFor="showLetterHead" style={{ fontWeight: '600' }}>Show letter head</label>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>Invoice Logo:</label>
                                                <input type="file" style={{ fontSize: '0.85rem' }} />
                                                <small style={{ display: 'block', color: '#94a3b8' }}>Max 1 MB, jpeg, gif, png formats only.</small>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="checkbox" id="showLogo" />
                                                <label htmlFor="showLogo" style={{ fontWeight: '600' }}>Show invoice Logo</label>
                                            </div>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>Header text:</label>
                                                <textarea style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px', height: '100px' }}></textarea>
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'labels' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            {['Invoice heading', 'Quotation Heading', 'Sales Order Heading', 'Invoice no. label', 'Date Label', 'Due date label', 'Sales Person Label'].map(label => (
                                                <div key={label}>
                                                    <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.4rem' }}>{label}:</label>
                                                    <input type="text" placeholder={label} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                            ))}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                                <input type="checkbox" id="showDueDate" />
                                                <label htmlFor="showDueDate" style={{ fontWeight: '600' }}>Show due date</label>
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'address' && (
                                        <div>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>Address Fields to Show:</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                                {['Landmark', 'City', 'State', 'Country', 'Zip Code', 'Mobile number', 'Email', 'Customer information', 'Client ID', 'Business name', 'Location name'].map(item => (
                                                    <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input type="checkbox" id={item} />
                                                        <label htmlFor={item} style={{ fontSize: '0.9rem' }}>Show {item}</label>
                                                    </div>
                                                ))}
                                            </div>
                                            <button style={{ color: '#3b82f6', border: 'none', background: 'none', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <span style={{ fontSize: '1.2rem' }}>+</span> Add more custom fields
                                            </button>
                                        </div>
                                    )}

                                    {section.id === 'products' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            {['Product Label', 'Quantity Label', 'Unit Price Label', 'Subtotal Label', 'HSN code label'].map(label => (
                                                <div key={label}>
                                                    <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.4rem' }}>{label}:</label>
                                                    <input type="text" placeholder={label} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                            ))}
                                            <div style={{ gridColumn: 'span 2', marginTop: '1rem' }}>
                                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>Product details to show:</h4>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                                    {['Brand', 'SKU', 'HSN Code', 'Sale description', 'Product description', 'Product image', 'Warranty name', 'Warranty expiry'].map(item => (
                                                        <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input type="checkbox" id={item} />
                                                            <label htmlFor={item} style={{ fontSize: '0.9rem' }}>Show {item}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'totals' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                            <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                                {['Subtotal label', 'Tax label', 'Total label', 'Round off label', 'Total items label'].map(label => (
                                                    <div key={label}>
                                                        <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.4rem' }}>{label}:</label>
                                                        <input type="text" placeholder={label} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#1e293b', fontSize: '0.95rem' }}>QR Code Settings</h4>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    {['Show QR Code', 'Show Labels', 'ZATCA (Fatoora) QR code'].map(item => (
                                                        <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input type="checkbox" id={item} />
                                                            <label htmlFor={item} style={{ fontSize: '0.875rem' }}>{item}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#1e293b', fontSize: '0.95rem' }}>Restaurant Module</h4>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                                    <input type="checkbox" id="showStaff" />
                                                    <label htmlFor="showStaff" style={{ fontSize: '0.875rem' }}>Show service staff</label>
                                                </div>
                                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '0.3rem', fontSize: '0.8rem' }}>Service staff label:</label>
                                                <input type="text" placeholder="Service staff" style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Modal Footer - Buttons in Middle */}
                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    <button style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        Save
                    </button>
                    <button onClick={() => setShowAddLayoutModal(false)} style={{ background: '#334155', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const toggleLayoutSelection = (id) => {
        setSelectedLayouts(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const renderLayoutsTable = () => (
        <div className="invoice-table-wrapper">
            <table className="invoice-table">
                <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        {['Layout Name', 'Design', 'Action'].map((header, idx) => (
                            <th key={idx} style={{ padding: '1rem', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    {header}
                                    {header !== 'Action' && <Info size={14} style={{ color: '#06b6d4' }} />}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {layouts.map((layout) => (
                        <tr key={layout.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <strong>{layout.name}</strong>
                                    {layout.isDefault && (
                                        <span style={{ background: '#10b981', color: 'white', fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Default</span>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '1rem' }}>Classic</td>
                            <td style={{ padding: '0.75rem 1rem' }} className="actions-cell">
                                <div className="action-dropdown-container">
                                    <button
                                        className="action-btn-trigger"
                                        onClick={(e) => toggleDropdown(`layout-${layout.id}`, e)}
                                    >
                                        Action <span className="arrow-down">▼</span>
                                    </button>
                                    {openDropdownId === `layout-${layout.id}` && (
                                        <div
                                            className="action-dropdown-menu"
                                            style={{
                                                position: 'fixed',
                                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                left: `${dropdownPosition.left}px`,
                                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                zIndex: 9999999
                                            }}
                                        >
                                            <button className="dropdown-item" onClick={() => setOpenDropdownId(null)}>
                                                <Edit3 size={14} style={{ marginRight: '8px' }} /> Edit
                                            </button>
                                            <button className="dropdown-item delete-item" onClick={() => setOpenDropdownId(null)}>
                                                <Trash2 size={14} style={{ marginRight: '8px' }} /> Delete
                                            </button>
                                            {!layout.isDefault && (
                                                <button className="dropdown-item" onClick={() => setOpenDropdownId(null)}>
                                                    <Info size={14} style={{ marginRight: '8px', color: '#06b6d4' }} /> Set as default
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="invoice-settings-container">
            <div className="invoice-settings-card">
                {/* Tabs Row with Integrated Search */}
                <div className="invoice-tabs-row">
                    <div className="invoice-tabs-nav">
                        <button
                            onClick={() => setActiveSubTab('schemes')}
                            className={`invoice-tab-btn ${activeSubTab === 'schemes' ? 'active' : 'inactive'}`}
                        >
                            Invoice Schemes
                        </button>
                        <button
                            onClick={() => setActiveSubTab('layouts')}
                            className={`invoice-tab-btn ${activeSubTab === 'layouts' ? 'active' : 'inactive'}`}
                        >
                            Invoice Layouts
                        </button>
                    </div>

                    <div className="invoice-search-container">
                        <div style={{ position: 'relative', width: '100%' }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="invoice-search-input"
                            />
                        </div>
                    </div>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Controls Row Below Tabs */}
                    <div className="invoice-controls-row">
                        <div className="invoice-entries-control">
                            <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Show</span>
                            <select style={{ padding: '0.3rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                            <span style={{ fontSize: '0.9rem', color: '#64748b' }}>entries</span>
                        </div>

                        <div className="invoice-actions-group">
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                                    style={{
                                        background: '#fff',
                                        color: '#64748b',
                                        border: '1px solid #e2e8f0',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Download size={16} /> Export <ChevronDown size={14} />
                                </button>
                                {showExportDropdown && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '160px', padding: '0.5rem 0' }}>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#10b981" /> Export CSV
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                                            <Printer size={16} color="#3b82f6" /> Print
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                                            <FileText size={16} color="#ef4444" /> Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => activeSubTab === 'schemes' ? setShowAddModal(true) : setShowAddLayoutModal(true)}
                                style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}
                            >
                                <span style={{ fontSize: '1.2rem', fontWeight: '400' }}>+</span> Add
                            </button>
                        </div>
                    </div>

                    {activeSubTab === 'schemes' ? renderSchemesTable() : renderLayoutsTable()}
                </div>

                {showAddModal && renderAddModal()}
                {showAddLayoutModal && renderAddLayoutModal()}
            </div>
        </div>
    );
};

export default InvoiceSettings;
